import { useMemo, useState } from "react";
import {
  TUTORIAL_LESSONS,
  describeSituation,
  getLesson,
  lessonIndex,
  nextLessonId,
} from "@kikik/mahjong-coach";
import {
  CoachPanel,
  Drawer,
  EndPanel,
  EventLog,
  GameShell,
  GameTable,
  HelpPanel,
  TableSettings,
} from "../components";
import type { ShellNav } from "../components";
import { useI18n, usePlayerNames } from "../i18n";
import { computeWaits } from "../game/tableModel";
import { useLocalGame } from "../game/useLocalGame";

export interface TutorialModeProps {
  lessonId?: string;
  botDelayMs?: number;
  /** Start on the lesson overview instead of the first lesson. */
  showOverview?: boolean;
  /** Supplied by the app shell; absent when a test renders the mode directly. */
  nav?: ShellNav;
}

const FIRST_LESSON = TUTORIAL_LESSONS[0]?.id ?? "dasar";

export function TutorialMode({ lessonId, botDelayMs, showOverview, nav }: TutorialModeProps) {
  const { t, tm, lesson: copyFor, lessonTitle } = useI18n();
  const names = usePlayerNames();
  const [activeId, setActiveId] = useState(lessonId ?? FIRST_LESSON);
  const [overview, setOverview] = useState(showOverview ?? false);
  const lesson = getLesson(activeId) ?? TUTORIAL_LESSONS[0];

  const game = useLocalGame({
    createState: useMemo(
      () => () => (lesson ? lesson.createState() : TUTORIAL_LESSONS[0]!.createState()),
      [lesson],
    ),
    autoAdvice: true,
    ...(botDelayMs === undefined ? {} : { botDelayMs }),
  });

  const nameFor = names.solo(game.you);
  const waits = useMemo(() => computeWaits(game.model), [game.model]);
  const done = lesson ? lesson.isComplete(game.state) : false;
  const next = lesson ? nextLessonId(lesson.id) : null;

  const openLesson = (id: string): void => {
    const target = getLesson(id);
    if (!target) return;
    setActiveId(id);
    setOverview(false);
    game.reset(target.createState());
  };

  const shell = {
    mode: "belajar" as const,
    onMode: nav?.onMode ?? ((): void => {}),
    onExit: nav?.onExit ?? ((): void => {}),
  };

  if (!lesson) return null;
  const copy = copyFor(lesson.id);
  if (!copy) return null;

  const lessonNav = (
    <nav className="lessons" aria-label={t("tutorial.nav")}>
      {TUTORIAL_LESSONS.map((item) => (
        <button
          key={item.id}
          type="button"
          data-lesson={item.id}
          className={`lessons__item ${item.id === lesson.id && !overview ? "lessons__item--active" : ""}`}
          onClick={() => openLesson(item.id)}
        >
          {lessonTitle(item.id)}
        </button>
      ))}
    </nav>
  );

  /*
   * The whole lesson, in the menu. What a learner needs *while their hand is on
   * a tile* is one sentence - the task - and that is banded across the top of
   * the felt instead. Objective, long explanation, the seven-lesson index and
   * the move-on buttons are all reading material, and reading material does not
   * get a permanent quarter of the table.
   */
  const lessonPanel = (
    <section className="panel lesson" data-lesson={lesson.id} data-testid="lesson-panel">
      <p className="lesson__progress">
        {t("tutorial.progress", {
          current: lessonIndex(lesson.id) + 1,
          total: TUTORIAL_LESSONS.length,
        })}
      </p>
      <h3>{copy.title}</h3>
      <p className="lesson__objective">
        <strong>{t("tutorial.objective")}:</strong> {copy.objective}
      </p>
      <p className="lesson__goal">
        <strong>{t("tutorial.task")}:</strong> {copy.goal}
      </p>

      <Drawer className="lesson__intro" title={t("tutorial.details")}>
        {copy.intro.map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
      </Drawer>

      {done ? (
        <div className="lesson__done" role="status">
          <p>{copy.success}</p>
          <div className="lesson__actions">
            {next ? (
              <button
                type="button"
                className="button button--primary"
                onClick={() => openLesson(next)}
              >
                {t("tutorial.next", { title: lessonTitle(next) })}
              </button>
            ) : (
              <span className="muted">{t("tutorial.allDone")}</span>
            )}
          </div>
          <p className="muted">{t("tutorial.keepPlaying")}</p>
        </div>
      ) : null}

      <div className="lesson__actions">
        <button
          type="button"
          className="button button--ghost"
          onClick={() => openLesson(lesson.id)}
        >
          {t("tutorial.replay")}
        </button>
        {next && !done ? (
          <button type="button" className="button button--ghost" onClick={() => openLesson(next)}>
            {t("tutorial.nextLesson")}
          </button>
        ) : null}
        <button type="button" className="button button--ghost" onClick={() => setOverview(true)}>
          {t("tutorial.back")}
        </button>
      </div>
    </section>
  );

  const menu = (
    <>
      <section className="menu-section">
        <h3 className="sheet__heading">{t("tutorial.title")}</h3>
        <p className="muted menu-section__note">{t("tutorial.subtitle")}</p>
        {lessonNav}
        <button
          type="button"
          className="button button--ghost"
          onClick={() => openLesson(FIRST_LESSON)}
        >
          {t("tutorial.restart")}
        </button>
      </section>

      {lessonPanel}

      <CoachPanel
        advice={game.advice}
        situation={describeSituation(game.state, game.you)}
        nameFor={nameFor}
        lessonHint={copy.coach}
        onAsk={game.showHint}
        {...(game.advice?.action
          ? { onFollow: () => game.submit(game.advice?.action as never) }
          : {})}
      />
      <EventLog entries={game.log} nameFor={nameFor} />
      <HelpPanel />
      <TableSettings showOpening={false} />
    </>
  );

  const notice = game.notice ? (
    <p className="notice" role="alert">
      {tm(game.notice, { playerName: nameFor })}
      <button type="button" className="notice__close" onClick={game.clearNotice}>
        {t("notice.close")}
      </button>
    </p>
  ) : null;

  // The overview: seven cards, so a learner can see the whole journey at once.
  if (overview) {
    return (
      <GameShell {...shell} variant="page" menu={menu} {...(notice ? { notice } : {})}>
        <div className="page">
          <header className="page__head">
            <h2>{t("tutorial.title")}</h2>
            <p className="muted">{t("tutorial.subtitle")}</p>
          </header>
          {lessonNav}
          <section className="panel lesson-overview">
            <h3>{t("tutorial.overview.title")}</h3>
            <p className="muted">{t("tutorial.overview.subtitle")}</p>
            <ol className="lesson-cards">
              {TUTORIAL_LESSONS.map((item, index) => {
                const itemCopy = copyFor(item.id);
                if (!itemCopy) return null;
                return (
                  <li className="lesson-card" key={item.id}>
                    <span className="lesson-card__number">
                      {t("tutorial.lessonNumber", { number: index + 1 })}
                    </span>
                    <h4>{itemCopy.title}</h4>
                    <p className="muted">{itemCopy.objective}</p>
                    <button
                      type="button"
                      className="button button--primary"
                      data-start-lesson={item.id}
                      onClick={() => openLesson(item.id)}
                    >
                      {t("tutorial.start")}
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>
        </div>
      </GameShell>
    );
  }

  return (
    <GameShell
      {...shell}
      menu={menu}
      {...(notice ? { notice } : {})}
      overlay={
        game.model.result ? (
          <EndPanel
            result={game.model.result}
            you={game.you}
            nameFor={nameFor}
            onRestart={() => openLesson(lesson.id)}
            restartLabel={t("tutorial.replay")}
            {...(next
              ? { onNewGame: () => openLesson(next), newGameLabel: t("tutorial.nextLesson") }
              : {})}
          />
        ) : null
      }
    >
      <GameTable
        model={game.model}
        nameFor={nameFor}
        waits={waits}
        // In a lesson the coach points at the tile it is talking about.
        hintTileId={game.advice?.tileId ?? game.hintTileId}
        onSubmit={game.submit}
        manualDraw
        onHint={game.showHint}
        suggested={game.advice?.action ?? null}
      >
        {/*
          * The one line of lesson that has to be on screen while playing, on
          * the near rail beside the hand it is talking about.
          */}
        <p
          className={`dock__lesson ${done ? "dock__lesson--done" : ""}`}
          data-testid="lesson-task"
          {...(done ? { role: "status" } : {})}
        >
          <span className="dock__lesson-step">
            {t("tutorial.progress", {
              current: lessonIndex(lesson.id) + 1,
              total: TUTORIAL_LESSONS.length,
            })}
          </span>
          {/* Passing a lesson is the whole point of the mode, so it is said at
              the table rather than only inside the menu the learner would have
              to open to find out they had won. */}
          <span className="dock__lesson-goal">{done ? copy.success : copy.goal}</span>
          {done && next ? (
            <button
              type="button"
              className="button button--primary dock__lesson-next"
              onClick={() => openLesson(next)}
            >
              {t("tutorial.next", { title: lessonTitle(next) })}
            </button>
          ) : null}
        </p>
      </GameTable>
    </GameShell>
  );
}
